/**
 * Feature E06-F01 — typed web api-client + TanStack mutations for the Research
 * Intake form. Thin wrappers over the shared `apiClient` speaking the exact
 * `@eer/shared` research-intake contracts the Fastify routes validate.
 *
 * Endpoints (packages/api/src/routes/research-intake.ts):
 *   POST /research/requests              → research_create (draft)        [T02]
 *   GET  /research/requests/:id          → research_get (open a draft)    [E12-F17-T07]
 *   PATCH /research/requests/:id         → research_update_draft          [E12-F17-T07]
 *   POST /research/requests/normalize     → research_criteria_normalize    [T05, id-less]
 *   POST /research/requests/:id/normalize → research_criteria_normalize    [T04, scoped]
 *   GET  /research/projects/:id/people    → recruiter + team leads          [T14]
 *
 * The owner is taken from the session (IDOR rule) — never the body. The intake
 * form sends `{ title, criteria, projectId?, reviewMode?, forceNew? }`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  researchGetResponseSchema,
  researchProjectPeopleResponseSchema,
  dnsClientsListResponseSchema,
  type ResearchCreateInput,
  type ResearchCreateResponse,
  type ResearchGetResponse,
  type ResearchUpdateDraftInput,
  type CriteriaNormalizeInput,
  type CriteriaNormalizeResponse,
  type ResearchProjectPeopleResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { researchKeys } from './researchLibraryApi.js';

/** POST /research/requests — create a draft research request from the intake form. */
export async function researchCreateApi(
  input: ResearchCreateInput,
): Promise<ResearchCreateResponse> {
  return unwrap(
    await apiClient.post<ResearchCreateResponse>('/research/requests', input, {
      command: 'research_create',
    }),
  );
}

/**
 * POST /research/requests(/:id)/normalize — AI-paste accelerator.
 *
 * When `requestId` is null/undefined the id-less sibling route
 * `POST /research/requests/normalize` is used (E06-F01-T05): it runs the exact
 * same normalize logic but requires NO draft request row, so the primary
 * "paste a raw brief onto an EMPTY form" flow works without first minting a
 * throwaway draft (which the create endpoint rejects on empty criteria). A
 * concrete `requestId` still targets the legacy scoped `:id` route.
 */
export async function criteriaNormalizeApi(
  input: CriteriaNormalizeInput,
  requestId?: number | null,
): Promise<CriteriaNormalizeResponse> {
  const path =
    requestId == null
      ? '/research/requests/normalize'
      : `/research/requests/${requestId}/normalize`;
  return unwrap(
    await apiClient.post<CriteriaNormalizeResponse>(path, input, {
      command: 'research_criteria_normalize',
    }),
  );
}

/**
 * research_create mutation (submit the intake form). Run / Save draft is the ONLY
 * path that persists a request row (E12-F17-T04), so it invalidates the cached
 * History pages — otherwise the 30s global staleTime hides the new row when the
 * recruiter goes straight back to History.
 */
export function useResearchCreate(): UseMutationResult<
  ResearchCreateResponse,
  ApiResultError,
  ResearchCreateInput
> {
  const queryClient = useQueryClient();
  return useMutation<ResearchCreateResponse, ApiResultError, ResearchCreateInput>({
    mutationFn: researchCreateApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...researchKeys.all, 'history'] });
    },
  });
}

/**
 * E12-F17-T07 — GET /research/requests/:id: the saved draft (title, criteria,
 * linked project) a History «Open → Request» re-opens the Intake form with.
 * Parsed through the shared schema so the criteria arrive with their defaults.
 */
export async function fetchResearchRequest(requestId: number): Promise<ResearchGetResponse> {
  const result = await apiClient.get<unknown>(`/research/requests/${requestId}`, {
    command: 'research_get',
  });
  return researchGetResponseSchema.parse(unwrap(result));
}

/** The saved request behind an opened draft; disabled while `requestId` is 0/null. */
export function useResearchRequest(
  requestId: number | null | undefined,
): UseQueryResult<ResearchGetResponse, ApiResultError> {
  const id = typeof requestId === 'number' && requestId > 0 ? requestId : 0;
  return useQuery<ResearchGetResponse, ApiResultError>({
    queryKey: [...researchKeys.all, 'request', id],
    queryFn: () => fetchResearchRequest(id),
    enabled: id > 0,
    // Always re-read on open: a draft edited a moment ago must not come back stale.
    staleTime: 0,
  });
}

/** Variables of the draft-update mutation. */
export interface UpdateDraftVars {
  readonly requestId: number;
  readonly input: ResearchUpdateDraftInput;
}

/** E12-F17-T07 — PATCH /research/requests/:id: Save draft / Run edit THAT draft. */
export async function researchUpdateDraftApi({
  requestId,
  input,
}: UpdateDraftVars): Promise<ResearchCreateResponse> {
  return unwrap(
    await apiClient.patch<ResearchCreateResponse>(`/research/requests/${requestId}`, input, {
      command: 'research_update_draft',
    }),
  );
}

/** research_update_draft mutation; refreshes History and the cached draft. */
export function useResearchUpdateDraft(): UseMutationResult<
  ResearchCreateResponse,
  ApiResultError,
  UpdateDraftVars
> {
  const queryClient = useQueryClient();
  return useMutation<ResearchCreateResponse, ApiResultError, UpdateDraftVars>({
    mutationFn: researchUpdateDraftApi,
    onSuccess: (_res, vars) => {
      void queryClient.invalidateQueries({ queryKey: [...researchKeys.all, 'history'] });
      void queryClient.invalidateQueries({
        queryKey: [...researchKeys.all, 'request', vars.requestId],
      });
    },
  });
}

/** Variables for the normalize mutation. `requestId` is optional (id-less T05). */
export interface NormalizeVars {
  readonly requestId?: number | null;
  readonly input: CriteriaNormalizeInput;
}

/** research_criteria_normalize mutation (AI-paste). */
export function useCriteriaNormalize(): UseMutationResult<
  CriteriaNormalizeResponse,
  ApiResultError,
  NormalizeVars
> {
  return useMutation<CriteriaNormalizeResponse, ApiResultError, NormalizeVars>({
    mutationFn: ({ requestId, input }) => criteriaNormalizeApi(input, requestId),
  });
}

/** GET /research/projects/:id/people — recruiter + team leads of a project (T14). */
export async function fetchResearchProjectPeople(
  projectId: number,
): Promise<ResearchProjectPeopleResponse> {
  const result = await apiClient.get<unknown>(`/research/projects/${projectId}/people`, {
    command: 'research_project_people',
  });
  return researchProjectPeopleResponseSchema.parse(unwrap(result));
}

/**
 * E07-F07-T03 — GET /admin/dns-clients (readable by any signed-in user) → the
 * number of ACTIVE global Do-Not-Source entries (EER clients), shown next to the
 * «Exclude EER clients» checkbox. null when the response is not the list shape.
 */
export async function fetchGlobalDnsActiveCount(): Promise<number | null> {
  const result = await apiClient.get<unknown>('/admin/dns-clients', {
    command: 'admin_dns_clients_list',
  });
  const parsed = dnsClientsListResponseSchema.safeParse(unwrap(result));
  return parsed.success ? parsed.data.activeCount : null;
}

/** The global DNS list size for the intake checkbox label (best-effort). */
export function useGlobalDnsCount(): UseQueryResult<number | null, ApiResultError> {
  return useQuery<number | null, ApiResultError>({
    queryKey: ['research', 'global-dns-count'],
    queryFn: fetchGlobalDnsActiveCount,
    staleTime: 60_000,
  });
}

/** The people of the linked project; disabled while no project is picked. */
export function useResearchProjectPeople(
  projectId: number | null | undefined,
): UseQueryResult<ResearchProjectPeopleResponse, ApiResultError> {
  const id = typeof projectId === 'number' && projectId > 0 ? projectId : 0;
  return useQuery<ResearchProjectPeopleResponse, ApiResultError>({
    queryKey: ['research', 'project-people', id],
    queryFn: () => fetchResearchProjectPeople(id),
    enabled: id > 0,
    staleTime: 60_000,
  });
}
